// EmploymentForm.tsx
// Form for adding/editing employment entries, mirrors EducationForm
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { monthInputToApiDate } from '@/lib/profile-dates';
import React from 'react';
import MonthYearSelect from '../MonthYearSelect';
import { EmploymentResponse } from '../EmploymentTab';

interface EmploymentFormProps {
  employment: EmploymentResponse;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCheckboxChange: (checked: boolean) => void;
  onCreate: () => void;
  reset: () => void;
}

const EmploymentForm: React.FC<EmploymentFormProps> = ({
  employment,
  isLoading,
  onChange,
  onCheckboxChange,
  onCreate,
  reset,
}) => (
  <div className="space-y-4 sm:space-y-6 mb-6 border-b pb-6">
    <h3 className="text-base sm:text-lg font-medium">Add New Employment</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="title">Job Title</Label>
        <Input
          id="title"
          name="title"
          value={employment.title}
          onChange={onChange}
          placeholder="e.g., Office Coordinator"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="company">Company</Label>
        <Input
          id="company"
          name="company"
          value={employment.company}
          onChange={onChange}
          placeholder="Company Name"
        />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="start_date">Start Month</Label>
        <MonthYearSelect
          value={employment.start_date}
          onChange={(value) => onChange({
            target: { name: 'start_date', value: monthInputToApiDate(value) },
          } as React.ChangeEvent<HTMLInputElement>)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="end_date">End Month (or Expected)</Label>
        <MonthYearSelect
          value={employment.end_date}
          onChange={(value) => onChange({
            target: { name: 'end_date', value: monthInputToApiDate(value) },
          } as React.ChangeEvent<HTMLInputElement>)}
          disabled={employment.is_ongoing}
        />
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        id="is_ongoing"
        name="is_ongoing"
        checked={employment.is_ongoing}
        onChange={(e) => onCheckboxChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-interview-blue focus:ring-interview-blue"
      />
      <Label htmlFor="is_ongoing" className="text-sm font-medium">
        Currently working here
      </Label>
    </div>
    <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2">
      <Button 
        variant="outline" 
        onClick={reset} 
        disabled={isLoading}
        className="w-full sm:w-auto order-2 sm:order-1"
      >
        Cancel
      </Button>
      <Button
        variant="default"
        onClick={onCreate}
        disabled={isLoading || !employment.title || !employment.company || !employment.start_date}
        className="w-full sm:w-auto order-1 sm:order-2"
      >
        {isLoading ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
);

export default EmploymentForm;
